/**
 * =============================================================================
 * PermissionsCenterDialog — Central de Permissões em formato de modal
 *
 * Substitui a antiga página com abas (/tenant/permissions) por um modal grande,
 * no formato do modal "Aplicativos e integrações", com cabeçalho editorial:
 *
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ Permissões / Perfis de Permissão                      ✕  │
 *   ├───────────────────┬──────────────────────────────────────┤
 *   │ Perfis            │                                      │
 *   │ Dashboards        │   conteúdo da seção escolhida        │
 *   │ Fases de Tarefas  │                                      │
 *   └───────────────────┴──────────────────────────────────────┘
 *
 * Regras preservadas da página anterior:
 *   - Cada seção só aparece se o usuário tiver permissão sobre ela;
 *   - Deep-link por ?tab=... continua funcionando (controlado pelo consumidor);
 *   - O conteúdo de cada seção é exatamente o componente já existente.
 *
 * CHANGELOG:
 *   RELEASE: 2026-09-08 v1.3.0 - Itens do menu lateral adotam o mesmo estilo do
 *            modal "Aplicativos e integrações" (altura 36px, item ativo com
 *            borda/cartão e barra vertical à esquerda, bloco "Seções").
 *   RELEASE: 2026-09-07 v1.2.0 - Remove a seta de voltar do cabeçalho; o
 *            fechamento permanece centralizado no botão X e no Dialog.
 *   RELEASE: 2026-09-07 v1.1.0 - Cabeçalho alinhado à faixa escura semântica do
 *            modal Aplicativos e integrações, incluindo controles de navegação.
 *   RELEASE: 2026-09-07 v1.0.0 - Criação inicial (Central de Permissões em modal).
 * =============================================================================
 */

import type { ComponentType, ReactNode } from 'react';
import { X } from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

/** Item de seção exibido no menu lateral do modal */
export interface PermissionsSection {
  id: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
}

interface PermissionsCenterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Seções visíveis (já filtradas por permissão pelo consumidor) */
  sections: PermissionsSection[];
  /** Seção ativa */
  activeSection: string;
  onSectionChange: (id: string) => void;
  /** Conteúdo da seção ativa */
  children: ReactNode;
}

export function PermissionsCenterDialog({
  open,
  onOpenChange,
  sections,
  activeSection,
  onSectionChange,
  children,
}: PermissionsCenterDialogProps) {
  const atual = sections.find((s) => s.id === activeSection);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        className="max-w-[95vw] w-[95vw] h-[95dvh] p-0 gap-0 overflow-hidden flex flex-col"
      >
        {/* Cabeçalho editorial sem navegação de retorno interna. */}
        <header className="shrink-0 bg-sidebar px-5 py-4 text-sidebar-foreground sm:px-7">
          <div className="flex items-start pr-10">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-sidebar-foreground/60">
                Empresa / Permissões
              </p>
              <DialogTitle className="mt-0.5 truncate text-xl font-semibold tracking-tight">
                {atual?.label ?? 'Permissões'}
              </DialogTitle>
              <DialogDescription className="text-xs text-sidebar-foreground/60">
                Configure perfis, dashboards e fases sem sair desta tela.
              </DialogDescription>
            </div>
          </div>

          <DialogClose className="absolute right-4 top-4 rounded-sm text-sidebar-foreground/70 opacity-80 transition-opacity hover:text-sidebar-foreground hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-sidebar-foreground/40">
            <X className="h-5 w-5" />
            <span className="sr-only">Fechar</span>
          </DialogClose>
        </header>

        <div className="flex min-h-0 flex-1 flex-col sm:flex-row">
          {/* ─────────── Menu lateral de seções ─────────── */}
          <aside className="flex w-full shrink-0 flex-col border-b bg-muted/30 sm:w-72 sm:border-b-0 sm:border-r">
            <ScrollArea className="max-h-[160px] flex-1 sm:max-h-none">
              {/* v1.3.0: itens no mesmo padrão do modal "Aplicativos e integrações" */}
              <nav className="space-y-1 p-3">
                <p className="px-3 pb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                  Seções
                </p>
                {sections.map((s) => {
                  const Icon = s.icon;
                  const ativo = s.id === activeSection;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => onSectionChange(s.id)}
                      className={cn(
                        'relative flex h-9 w-full items-center gap-2 rounded-md px-3 text-left text-sm transition-colors',
                        ativo
                          ? 'border border-border bg-card font-semibold text-foreground shadow-sm'
                          : 'text-foreground/80 hover:bg-muted',
                      )}
                    >
                      {ativo && (
                        <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-foreground" />
                      )}
                      <Icon className={cn('h-3.5 w-3.5 shrink-0', ativo && 'ml-1')} />
                      <span className="min-w-0 flex-1 truncate">{s.label}</span>
                    </button>
                  );
                })}
              </nav>
            </ScrollArea>
          </aside>

          {/* ─────────── Conteúdo da seção (única área com rolagem) ─────────── */}
          <div className="min-w-0 flex-1 overflow-y-auto p-5 sm:p-6">{children}</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
